// Inline inter-trial survey (INTER_TRIAL_SURVEY). Shown over the chat every N
// replies. It fetches the chat block's survey, renders the shared SurveyForm in
// an overlay card, logs the answers against the block, then closes so the chat
// resumes (or the block advances if that reply also ended it).
//
// Behavior (per the agreed design):
//   - The card overlays the chat but does not fully cover it; the messages stay
//     visible behind a light backdrop.
//   - inter_trial_scroll_lock controls the background: locked = the backdrop
//     captures input so the chat behind cannot be scrolled; unlocked = pointer
//     events pass through the backdrop so the participant can still scroll the
//     chat behind the card.
//   - There is no cancel/close. The Submit button is disabled until every
//     question is answered; submitting is the only way out.
//   - A TIME trigger ("once, N seconds into the chat") reuses this same overlay:
//     it passes its trigger so the server resolves that trigger's survey. If the
//     server hands back a different survey (a backend that does not know time
//     triggers yet answers with the message-count survey), we close without
//     showing it rather than ask the wrong questions.

import { useEffect, useState } from "react";
import type { SessionHandle } from "../../session/sessionDriver";
import { fetchChatSurvey, submitChatSurvey } from "../../session/sessionDriver";
import type { SurveyQuestion } from "../../api/types";
import { SurveyForm, type SurveyResponse } from "../survey/SurveyForm";

export default function InterTrialSurvey({
  session,
  scrollLock,
  onClose,
  trigger,
}: {
  session: SessionHandle;
  scrollLock: boolean;
  onClose: () => void;
  trigger?: { id: string; surveyId: string } | undefined;
}) {
  const [questions, setQuestions] = useState<SurveyQuestion[] | null>(null);
  const [surveyName, setSurveyName] = useState("");
  const [done, setDone] = useState(false); // failed-or-empty -> close

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // The count path keeps its original call shape (no trigger id).
        const r = trigger?.id
          ? await fetchChatSurvey(session, trigger.id)
          : await fetchChatSurvey(session);
        if (!alive) return;
        if (trigger && String(r.survey_id ?? "") !== trigger.surveyId) {
          setDone(true); // not this trigger's survey: skip it
          return;
        }
        setSurveyName(r.name || "");
        setQuestions(r.questions || []);
      } catch {
        if (!alive) return;
        setDone(true); // could not load: don't trap the participant
      }
    })();
    return () => {
      alive = false;
    };
  }, [session]);

  // If the survey could not load or has no questions, close without blocking.
  useEffect(() => {
    if (done) onClose();
    else if (questions !== null && questions.length === 0) onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done, questions]);

  async function handleSubmit(responses: SurveyResponse[]) {
    try {
      if (trigger?.id) await submitChatSurvey(session, surveyName, responses, trigger.id);
      else await submitChatSurvey(session, surveyName, responses);
    } catch {
      // Even if logging fails we still resume the chat rather than trap them;
      // the response loss is surfaced server-side via the failed request.
    }
    onClose();
  }

  // Nothing visible while loading or when there is nothing to ask.
  if (questions === null || questions.length === 0) return null;

  // The backdrop keeps the .jol-survey class so the shared input styling (which
  // is scoped under .jol-survey) still applies, but its full-screen white
  // layout is overridden inline to a dim, centered overlay. The visible window
  // is the .jol-survey-card (rendered by SurveyForm), content-sized and centered
  // both ways; the wrapper only caps its width and keeps it interactive.
  return (
    <div
      className="jol-survey jol-its-overlay"
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        zIndex: 60,
        background: "rgba(0, 0, 0, 0.18)",
        // Locked: the backdrop captures events so the chat behind cannot be
        // scrolled. Unlocked: events pass through so the chat behind stays
        // scrollable. The card itself is always interactive.
        pointerEvents: scrollLock ? "auto" : "none",
      }}
    >
      <div style={{ pointerEvents: "auto", width: "min(560px, 100%)" }}>
        <SurveyForm questions={questions} submitLabel="Submit" onSubmit={handleSubmit} />
      </div>
    </div>
  );
}
